import { FileField } from '@ff-it/form';
import { Box, BoxProps, Button } from '@ff-it/ui';
import { useEntity } from 'components';
import { Enterprise } from 'modules/core/types';
import { ReactElement, ReactNode } from 'react';
import { api } from 'services';
import { KeyedMutator } from 'swr';
import { BrandingResponse } from './type';

interface Dimensions {
  width: number;
  height: number;
}

function Placehder({ width, height }: Dimensions): ReactElement {
  return (
    <Box p="1" boxShadow="lg" display="inline-block" border="default" borderRadius="xs" background="secondary">
      <svg
        width={width}
        height={height}
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid slice"
        textAnchor="middle"
      >
        <rect width="100%" height="100%" fill="#868e96"></rect>
        <text x="50%" y="50%" fill="#dee2e6">
          {width}x{height}
        </text>
      </svg>
    </Box>
  );
}

function Image({ width, height, src, ...rest }: Dimensions & { src: string } & BoxProps): ReactElement {
  return (
    <Box p="1" boxShadow="lg" display="inline-block" border="default" borderRadius="xs" {...rest}>
      <img src={src} width={width} height={height} />
    </Box>
  );
}

function LogoField({
  label,
  url,
  name,
  mutate,
  ...dims
}: Dimensions & {
  name: string;
  label: ReactNode;
  url: string | null;
  mutate: KeyedMutator<BrandingResponse>;
}): ReactElement {
  const { endpoint } = useEntity<Enterprise>();

  return (
    <Box display="flex" flexDirection="column" gap="1" alignItems="flex-start">
      <label>{label}</label>
      {url ? (
        <Box position="relative">
          <Image src={url} {...dims} background={name === 'logo' ? 'gray800' : 'secondary'} />
          <Button
            variant="danger"
            type="button"
            size="xs"
            style={{
              position: 'absolute' as const,
              right: 0,
              top: 0,
            }}
            onClick={() => {
              if (confirm('Are you sure you want to delete this logo?')) {
                api.delete(`${endpoint}logos/${name}/`).then((res) => {
                  if (res.ok) {
                    mutate(
                      (res) => {
                        if (res) return { ...res, [`${name}_url`]: null };
                      },
                      { revalidate: false },
                    );
                  } else {
                    throw res.error;
                  }
                });
              }
            }}
            icon="xmark"
          />
        </Box>
      ) : (
        <Placehder {...dims} />
      )}

      <FileField name={name} />
    </Box>
  );
}

export function Logos({
  logo_url,
  invoice_logo_url,
  mutate,
}: {
  logo_url: string | null;
  invoice_logo_url: string | null;
  mutate: KeyedMutator<BrandingResponse>;
}): ReactElement {
  return (
    <Box>
      <legend>Logos</legend>
      <LogoField label="Brand logo" name="logo" url={logo_url} width={200} height={60} mutate={mutate} />
      <LogoField
        label="Invoice and XLSX logo"
        name="invoice_logo"
        url={invoice_logo_url}
        width={400}
        height={120}
        mutate={mutate}
      />
    </Box>
  );
}
